/**
 * Token Storage Utility
 * - Refresh Token: Stored in Cookie (`refresh_token`) with LocalStorage fallback
 * - Access Token: In-Memory ONLY (Zustand store state fields `token` and `accessToken`)
 */

export const REFRESH_TOKEN_COOKIE_NAME = 'refresh_token'
export const ACCESS_TOKEN_STORAGE_KEY = 'access_token'

/**
 * Set Refresh Token in Cookie (document.cookie) and LocalStorage fallback
 */
export function setRefreshTokenCookie(token: string, days = 30): void {
  if (typeof document !== 'undefined') {
    const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString()
    const secureFlag = window.location.protocol === 'https:' ? '; Secure' : ''
    document.cookie = `${REFRESH_TOKEN_COOKIE_NAME}=${encodeURIComponent(token)}; expires=${expires}; path=/; SameSite=Lax${secureFlag}`
    document.cookie = `refreshToken=${encodeURIComponent(token)}; expires=${expires}; path=/; SameSite=Lax${secureFlag}`
  }
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(REFRESH_TOKEN_COOKIE_NAME, token)
    localStorage.setItem('refreshToken', token)
  }
}

/**
 * Get Refresh Token from Cookie (with fallback to LocalStorage)
 */
export function getRefreshToken(): string | null {
  if (typeof document !== 'undefined' && document.cookie) {
    const cookies = document.cookie.split(';')
    for (const cookie of cookies) {
      const parts = cookie.trim().split('=')
      if (parts.length >= 2) {
        const key = parts[0].trim().toLowerCase()
        const value = parts.slice(1).join('=').trim()
        if ((key === 'refresh_token' || key === 'refreshtoken' || key === 'token') && value) {
          return decodeURIComponent(value)
        }
      }
    }
  }
  if (typeof localStorage !== 'undefined') {
    const fromStorage =
      localStorage.getItem(REFRESH_TOKEN_COOKIE_NAME) ||
      localStorage.getItem('refreshToken') ||
      localStorage.getItem('token')
    if (fromStorage) return fromStorage
  }
  return null
}

/**
 * Remove Refresh Token from Cookie & LocalStorage
 */
export function removeRefreshTokenCookie(): void {
  if (typeof document !== 'undefined') {
    document.cookie = `${REFRESH_TOKEN_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
    document.cookie = `refreshToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
    document.cookie = `token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
  }
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(REFRESH_TOKEN_COOKIE_NAME)
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('token')
  }
}

/**
 * Set Access Token (In-memory token management handled via Zustand)
 */
export function setAccessToken(token: string): void {
  if (typeof localStorage !== 'undefined') {
    if (token) {
      localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, token)
      localStorage.setItem('auth_token', token)
    } else {
      localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY)
      localStorage.removeItem('auth_token')
    }
  }
}

/**
 * Get Access Token (checks LocalStorage fallback if not in Zustand memory)
 */
export function getAccessToken(): string | null {
  if (typeof localStorage !== 'undefined') {
    return localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY) || localStorage.getItem('auth_token') || null
  }
  return null
}

/**
 * Remove Access Token from LocalStorage
 */
export function removeAccessToken(): void {
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY)
    localStorage.removeItem('auth_token')
  }
}

// Backwards compatibility functions
export const setAccessTokenCookie = setAccessToken
export const setRefreshToken = setRefreshTokenCookie
export const removeAccessTokenCookie = removeAccessToken
export const removeRefreshToken = removeRefreshTokenCookie

/**
 * Save Auth Tokens (Access Token in localStorage & Zustand memory, Refresh Token in Cookie)
 */
export function saveAuthTokens(accessToken?: string, refreshToken?: string): void {
  if (accessToken) {
    setAccessToken(accessToken)
    if (typeof localStorage !== 'undefined') {
      try {
        const parts = accessToken.split('.')
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')))
          if (payload?.orgId) {
            localStorage.setItem('active_org_id', payload.orgId)
            localStorage.setItem('org_id', payload.orgId)
          }
          if (Array.isArray(payload?.branchIds) && payload.branchIds.length > 0) {
            localStorage.setItem('active_branch_id', payload.branchIds[0])
            localStorage.setItem('branch_id', payload.branchIds[0])
          }
          if (Array.isArray(payload?.permissions) && payload.permissions.length > 0) {
            localStorage.setItem('school-erp-permissions', JSON.stringify(payload.permissions))
          }
        }
      } catch {
        // ignore non-JWT payload parsing errors
      }
    }
  }
  if (refreshToken) {
    setRefreshTokenCookie(refreshToken)
  } else if (accessToken) {
    setRefreshTokenCookie(accessToken)
  }
}

/**
 * Clear All Auth Tokens
 */
export function clearAuthTokens(): void {
  removeAccessToken()
  removeRefreshTokenCookie()
}


